import { type ReactNode, type RefObject, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../lib/cn'
import { currencyLabel, formatAmount } from '../lib/format'
import { useLanguage } from '../lib/i18n'
import { RialSign } from './rial-sign'

export interface AmountProps {
  /** Decimal string as it travels over the API (never a JS number). */
  value: string | null | undefined
  /** Fraction digits; OMR uses 3 (baisa). */
  scale?: number
  /** Currency shown before the figure in muted ink: "OMR" is the rial sign (CurrencyLabel). */
  currency?: string
  /**
   * `figure`: a standalone figure (a stat tile), in proportional digits, which read better large;
   * the default sets tabular digits so a column's decimals line up.
   */
  variant?: 'table' | 'figure'
  className?: string
}

/**
 * An amount in accountant's convention: tabular figures, fixed 3dp, negatives in red ink with a
 * minus sign. Always laid out left-to-right (Latin digits, sign first) and aligned to the right of
 * its box. In a table's amount column (`AMOUNT_CELL`) that box has a fixed width at the column's
 * end, so the decimal points line up in both languages. No value shows a muted dash in the same box.
 */
export function Amount({ value, scale, currency, variant = 'table', className }: AmountProps) {
  if (value === null || value === undefined || value === '') {
    return (
      <span
        data-amount
        dir="ltr"
        className={cn('inline-block text-right whitespace-nowrap text-ink-muted', className)}
      >
        —
      </span>
    )
  }
  const { text, negative } = formatAmount(value, scale)
  // The baisa read quieter than the rials, so a column scans by its whole units first (the text
  // itself is unchanged: copy, search and screen readers get the full figure). Only a figure in plain
  // ink sets them in muted ink; one already toned (zero, negative or a caller's colour) keeps its tone
  // whole, because a dimmed muted or red ink falls under 4.5:1.
  const point = text.lastIndexOf('.')
  const whole = point === -1 ? text : text.slice(0, point)
  const fraction = point === -1 ? '' : text.slice(point)
  const zero = /^[−-]?0(\.0+)?$/.test(text)
  const plain = !negative && !zero && cn('text-ink', className).split(' ').includes('text-ink')
  return (
    <span
      data-amount
      dir="ltr"
      data-negative={negative || undefined}
      data-zero={zero || undefined}
      className={cn(
        'inline-block text-right whitespace-nowrap',
        variant === 'table' && 'tabular-nums',
        negative ? 'text-ink-red' : zero ? 'text-ink-muted' : 'text-ink',
        className,
      )}
    >
      {currency ? <CurrencyLabel code={currency} /> : null}
      {whole}
      {fraction ? (
        <span data-fraction className={plain ? 'text-ink-muted' : undefined}>
          {fraction}
        </span>
      ) : null}
    </span>
  )
}

/**
 * On a table cell (or a whole table) whose column holds amounts: every `Amount` inside becomes a
 * fixed-width box (13ch by default, `--amount-ch` to change it) whose figures are right-aligned. The
 * cell aligns to its end, so the box sits at the column's end and the decimals line up whether the
 * page runs left-to-right or right-to-left. The DataTable applies it to every end-aligned column.
 */
export const AMOUNT_CELL = '[&_[data-amount]]:min-w-[var(--amount-ch,13ch)]'

/**
 * On a hand-written table (a statement, a report, document lines): the same fixed-width box for
 * every `Amount` in an end-aligned cell (`text-end`), so those columns line up as the DataTable's do.
 */
export const AMOUNT_TABLE = '[&_.text-end_[data-amount]]:min-w-[var(--amount-ch,13ch)]'

/**
 * The head of an amount column (in an end-aligned `th`): its label set in the same fixed-width box as
 * the column's figures and right-aligned like them, so it stands over them in both languages. Alone,
 * an end-aligned head sits at the cell's far end: in Arabic that is the figures' left, up to a box's
 * width away from them. The box takes its `ch` in the figures' size and weight (text-sm, normal), so it
 * is exactly as wide as theirs; the label keeps the head's (text-xs semibold, `labelClassName`).
 */
export function AmountHead({
  children,
  className,
  labelClassName,
}: {
  children: ReactNode
  className?: string
  labelClassName?: string
}) {
  return (
    <span
      data-amount-head
      className={cn(
        'inline-block min-w-[var(--amount-ch,13ch)] text-right text-sm leading-none font-normal',
        className,
      )}
    >
      <span className={cn('text-xs leading-4 font-semibold', labelClassName)}>{children}</span>
    </span>
  )
}

/**
 * The columns of a rendered table whose body cells hold an `Amount` (cells marked
 * `data-amount-column="<column id>"`), so a table that does not know what its cells render can put
 * those heads, and only those, in `AmountHead`. A count or a quantity in plain text keeps its head at
 * the cell's end, where its value is. Rechecked after every render; it updates only on a change.
 */
export function useAmountColumns(ref: RefObject<HTMLElement | null>): ReadonlySet<string> {
  const [columns, setColumns] = useState<ReadonlySet<string>>(NO_COLUMNS)
  useLayoutEffect(() => {
    const found = new Set<string>()
    for (const cell of ref.current?.querySelectorAll<HTMLElement>('tbody [data-amount-column]') ?? []) {
      const id = cell.dataset.amountColumn
      if (id && !found.has(id) && cell.querySelector('[data-amount]')) found.add(id)
    }
    setColumns((prev) => (prev.size === found.size && [...found].every((id) => prev.has(id)) ? prev : found))
  })
  return columns
}

const NO_COLUMNS: ReadonlySet<string> = new Set()

/**
 * The currency before a figure. The rial is its sign (the Central Bank of Oman's), as large as the
 * figure and named "Omani rial" to assistive technology; any other code is written out. Inside an
 * Amount (always left to right) it stands on the figure's left in both languages, as the bank sets it.
 */
export function CurrencyLabel({ code, className }: { code: string; className?: string }) {
  const { language } = useLanguage()
  const { t } = useTranslation()
  if (code === 'OMR') {
    return (
      <>
        <span
          role="img"
          aria-label={t('currency.omr')}
          data-currency="OMR"
          className={cn('me-1 inline-flex self-baseline text-ink-muted', className)}
        >
          <RialSign />
        </span>
        {/* For the ear, not the eye: a name built from the content reads "Omani rial 75.000". */}
        <span className="sr-only"> </span>
      </>
    )
  }
  return <span className={cn('me-1 text-xs text-ink-muted', className)}>{currencyLabel(code, language)}</span>
}

/**
 * An amount inside a sentence ("Pay out [rial sign] 12.000 in cash?"): the sign on the figure's left in
 * both languages, the two kept together and isolated from the sentence's direction. Sentences built
 * with `t()` mark their amounts instead (`moneyMark`, rendered here by `withMoney`, ui/money-text).
 */
export function Money({ value, className }: { value: string; className?: string }) {
  return (
    <bdi dir="ltr" className={cn('inline-flex items-baseline whitespace-nowrap tabular-nums', className)}>
      <CurrencyLabel code="OMR" />
      {formatAmount(value).text}
    </bdi>
  )
}
